/**
 * Key derivation for TENANT-AUTHORED form content — the pure half of
 * `dynamicTranslation.ts`.
 *
 * Split out deliberately: the hook imports TranslationContext, which
 * transitively touches `localStorage` at module load, so anything importing it
 * needs a DOM. These functions are the part worth unit-testing, and they need
 * nothing.
 *
 * WHY A RAW LABEL IS NEVER USED AS A KEY
 * --------------------------------------
 * i18next runs with the default `keySeparator: '.'` and `nsSeparator: ':'`, and
 * `es.json` is genuinely nested across 150+ groups. Passing a human string as a
 * key mangles it: "Note: bring ID" is parsed as namespace "Note" and returns
 * " bring ID"; a label with a period loses everything before the last dot, and
 * can even resolve to an unrelated string that happens to live at that path. So
 * keys are derived and slugified, and the tenant's own text is the FALLBACK.
 *
 * THE KEY NAMES ARE THE BACKEND'S, NOT OURS
 * -----------------------------------------
 * `app/services/dynamic_translations.py` registers the keys and names them:
 *
 *   dyn.cbo284.com_field.sex_at_birth.label
 *   dyn.cbo284.com_field.sex_at_birth.option.male
 *   dyn.cbo284.form.112.field.medi_cal_cin.help_text
 *   dyn.cbo284.form.112.section.member_information.title
 *   dyn.cbo284.public_form.7.success_message
 *
 * Two of those components are ids the browser does not have: an anonymous
 * visitor on `/public/forms/<token>` is never told the org id or the template
 * id. Rather than plumb both through every payload just to rebuild a string, the
 * bundle is indexed by ALIAS — the key with its `dyn.cboN.` prefix and any
 * numeric components removed — and looked up by the part that carries meaning.
 * Aliasing also means renumbering a template, or the same field appearing on two
 * forms, does not silently stop resolving.
 */

/** Longest single dotted component. Matches MAX_SLUG_COMPONENT server-side. */
export const MAX_SLUG_COMPONENT = 48;

export interface FieldRef {
  field_id?: string;
  data_source?: string | null;
}

/**
 * Lowercase ASCII slug for one dotted component.
 *
 * Mirrors `slugify` in app/services/dynamic_translations.py: accent-fold, lower,
 * collapse every run of non-alphanumerics to one underscore, trim, truncate. If
 * the two ever disagree, keys stop resolving silently — which is why the length
 * cap is a shared named constant rather than an inline number.
 */
export function slugify(value: string, maxLength: number = MAX_SLUG_COMPONENT): string {
  const slug = String(value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // fold accents: "Año" and "Ano" must key alike
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug.slice(0, maxLength).replace(/_+$/g, '');
}

/** The com_field name behind a form field: `member.custom_fields.x` -> `x`. */
export function fieldKeyOf(field: FieldRef): string {
  const source = field?.data_source || '';
  if (source.startsWith('member.custom_fields.')) return source.slice('member.custom_fields.'.length);
  if (source.startsWith('member.')) return source.slice('member.'.length);
  return field?.field_id || '';
}

const DYN_PREFIX = /^dyn\.cbo\d+\./;
const NUMERIC_COMPONENT = /^\d+$/;

/**
 * Index a `{key_name: text}` bundle by alias, so lookups need no org id and no
 * template id. Later keys do not clobber earlier ones: a form-scoped key and a
 * com_field key can alias to different things, and both are kept.
 */
export function indexDynamicBundle(bundle: Record<string, string> | null | undefined): Record<string, string> {
  const index: Record<string, string> = {};
  if (!bundle) return index;
  for (const [key, text] of Object.entries(bundle)) {
    if (!text) continue;
    index[key] = text; // exact key still wins if a caller ever has one
    const withoutPrefix = key.replace(DYN_PREFIX, '');
    if (!(withoutPrefix in index)) index[withoutPrefix] = text;
    const withoutIds = withoutPrefix
      .split('.')
      .filter((part) => !NUMERIC_COMPONENT.test(part))
      .join('.');
    if (!(withoutIds in index)) index[withoutIds] = text;
  }
  return index;
}

/**
 * Candidate aliases for one attribute of a form field, most specific first.
 *
 * The form-scoped key wins over the com_field key because a label overridden on
 * one form should not leak to every other form using the same field.
 */
export function fieldKeyCandidates(field: FieldRef, attr: string): string[] {
  const formSlug = slugify(field?.field_id || '');
  const masterSlug = slugify(fieldKeyOf(field));
  const candidates = [`form.field.${formSlug}.${attr}`];
  if (masterSlug && masterSlug !== formSlug) candidates.push(`com_field.${masterSlug}.${attr}`);
  else candidates.push(`com_field.${formSlug}.${attr}`);
  return candidates;
}

export function optionKeyCandidates(field: FieldRef, optionValue: string): string[] {
  return fieldKeyCandidates(field, `option.${slugify(optionValue)}`);
}

export function sectionKeyCandidates(sectionId: string, attr: 'title' | 'description'): string[] {
  return [`form.section.${slugify(sectionId)}.${attr}`];
}

/** Public-form chrome the tenant wrote: title, blurb, thank-you message. */
export function publicFormKeyCandidates(attr: 'title' | 'description' | 'success_message'): string[] {
  return [`public_form.${attr}`];
}

export interface DynamicTranslator {
  /** Resolve a registered key, falling back to the tenant's own text. */
  label: (field: FieldRef, raw?: string | null) => string;
  /** Field help text — registered server-side as `help_text`. */
  help: (field: FieldRef, raw?: string | null) => string;
  description: (field: FieldRef, raw?: string | null) => string;
  placeholder: (field: FieldRef, raw?: string | null) => string;
  option: (field: FieldRef, optionValue: string, raw?: string | null) => string;
  sectionTitle: (sectionId: string, raw?: string | null) => string;
  sectionDescription: (sectionId: string, raw?: string | null) => string;
  publicForm: (attr: 'title' | 'description' | 'success_message', raw?: string | null) => string;
  /** App-chrome strings ("Submit", "Your Name"). Plain t() with a fallback. */
  chrome: (key: string, fallback: string) => string;
  language: string;
}

/**
 * Used when there is no TranslationProvider above the renderer, so a preview
 * surface or a bare test still renders the tenant's own text.
 */
export const identityTranslator: DynamicTranslator = {
  label: (_f, raw) => raw ?? '',
  help: (_f, raw) => raw ?? '',
  description: (_f, raw) => raw ?? '',
  placeholder: (_f, raw) => raw ?? '',
  option: (_f, optionValue, raw) => raw ?? optionValue ?? '',
  sectionTitle: (_s, raw) => raw ?? '',
  sectionDescription: (_s, raw) => raw ?? '',
  publicForm: (_a, raw) => raw ?? '',
  chrome: (_k, fallback) => fallback,
  language: 'en',
};
